import React, { Component, type ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import { createRootRoute, createRoute, createRouter, RouterProvider } from '@tanstack/react-router';
import { ConvexReactClient, Authenticated, Unauthenticated, AuthLoading } from 'convex/react';
import { ConvexAuthProvider, useAuthActions } from '@convex-dev/auth/react';
import { Application } from './App';
import { DEMO } from './shared';
import { Button } from './components/ui/button';
import { Logo } from './components/common';
import './styles.css';
const root = createRootRoute({ component: Application });
const routes = [
  createRoute({ getParentRoute: () => root, path: '/' }),
  createRoute({ getParentRoute: () => root, path: '/series/$projectId' }),
  ...(
    [
      'community',
      'community/ideas',
      'community/people',
      'community/voting',
      'community/moderation',
    ] as const
  ).map((path) =>
    createRoute({
      getParentRoute: () => root,
      path: `/${path}`,
      validateSearch: (search: Record<string, unknown>) => ({
        qstate: typeof search.qstate === 'string' ? search.qstate : 'pending',
        q: typeof search.q === 'string' ? search.q : '',
        filter: typeof search.filter === 'string' ? search.filter : '',
        period: typeof search.period === 'string' ? search.period : '',
        genre: typeof search.genre === 'string' ? search.genre : '',
        past: String(search.past ?? 'false'),
      }),
    }),
  ),
  ...(['cast', 'production', 'experiment', 'settings', 'kids'] as const).map((path) =>
    createRoute({ getParentRoute: () => root, path: `/${path}` }),
  ),
];
const router = createRouter({ routeTree: root.addChildren(routes) });
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
function SignIn() {
  const { signIn } = useAuthActions();
  const [error, setError] = React.useState('');
  const [pending, setPending] = React.useState(false);
  return (
    <div className="auth-screen">
      <Logo />
      <span className="eyebrow">CLAPTAL LAB · PRIVATE STUDIO</span>
      <h1>
        A story only
        <br />
        you can tell.
      </h1>
      <p>
        Your production room is waiting.
        <br />
        Sign in with your private Lab account.
      </p>
      <form
        className="login-form"
        onSubmit={async (event) => {
          event.preventDefault();
          if (pending) return;
          const data = new FormData(event.currentTarget);
          data.set('flow', 'signIn');
          setError('');
          setPending(true);
          try {
            await signIn('password', data);
          } catch {
            setError('Unable to sign in. Check your email and password, or contact the Lab owner.');
          } finally {
            setPending(false);
          }
        }}
      >
        <label htmlFor="login-email">Email</label>
        <input id="login-email" name="email" type="email" autoComplete="username" required />
        <label htmlFor="login-password">Password</label>
        <input
          id="login-password"
          name="password"
          type="password"
          autoComplete="current-password"
          maxLength={256}
          required
        />
        <Button type="submit" disabled={pending}>
          {pending ? 'Signing in…' : 'Sign in'}
        </Button>
        <small>Contact the Lab owner for access or a password reset.</small>
      </form>
      {error && (
        <p role="alert" className="error-text">
          {error}
        </p>
      )}
      <small>Imagine a story. Make it a series. Find an audience.</small>
    </div>
  );
}
class Boundary extends Component<{ children: ReactNode }, { error: string }> {
  state = { error: '' };
  static getDerivedStateFromError(e: Error) {
    return { error: e.message };
  }
  render() {
    return this.state.error ? (
      <div className="auth-screen">
        <h1>We couldn’t open the Lab.</h1>
        <p>{this.state.error}</p>
        <Button onClick={() => location.reload()}>Try again</Button>
      </div>
    ) : (
      this.props.children
    );
  }
}
const url = import.meta.env.VITE_CONVEX_URL;
const convexClient = url ? new ConvexReactClient(url) : undefined;
function Root() {
  if (DEMO) return <RouterProvider router={router} />;
  if (!url)
    return (
      <div className="auth-screen">
        <span className="eyebrow">CLAPTAL LAB</span>
        <h1>
          Your studio,
          <br />
          ready to connect.
        </h1>
        <p>
          Set VITE_CONVEX_URL to connect the private workspace.
          <br />
          For an offline walkthrough, run <code>pnpm demo</code>.
        </p>
        <small>Production authentication is never bypassed automatically.</small>
      </div>
    );
  return (
    <ConvexAuthProvider client={convexClient!}>
      <AuthLoading>
        <div className="loading">Opening your workspace…</div>
      </AuthLoading>
      <Unauthenticated>
        <SignIn />
      </Unauthenticated>
      <Authenticated>
        <RouterProvider router={router} />
      </Authenticated>
    </ConvexAuthProvider>
  );
}
if (import.meta.env.VITE_POSTHOG_KEY && !DEMO) {
  void import('posthog-js').then(({ default: posthog }) =>
    posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
      api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com',
      autocapture: false,
      capture_pageview: false,
      disable_session_recording: true,
      persistence: 'memory',
    }),
  );
}
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Boundary>
      <Root />
    </Boundary>
  </React.StrictMode>,
);
